import { Badge } from "@digital-services-gard-as/anchor-react";
import { useNavigate } from "react-router-dom";

import { InvoiceFromOcrDto } from "@/api/invoiceApi";
import {
    AttachmentDto,
    BankAccountDto,
    ClaimsDetailsDto,
    InsertInvoiceDto,
    InvoiceAttachment,
    InvoiceDetailsDto,
    InvoiceFormData,
    invoiceStatusValues
} from "@/types";
import { useBroadcastChannel } from "@/hooks/useBroadcastChannel";
import { convertCostToNumber } from "./costUtils";
import { pickSubClaim } from "./subClaimUtils";
import i18n from "../../i18n";

export type ocrBankAccountType = {
    accountNumber?: string;
    currency?: string;
};

type StatusBadgeProps = {
    id: string;
    invoiceStatus: string;
    size?: "sm" | "lg";
};

type GetSubclaimRefParams = {
    claim?: ClaimsDetailsDto;
    invoiceDetails: InvoiceDetailsDto;
    ocrData?: InvoiceFromOcrDto;
    assignedSubclaimRefs?: string[];
};

// Status mapping must be done in both backend and frontend so the table and export stay in sync.
const getDisplayInvoiceStatus = (invoiceStatus: string) => {
    if (
        invoiceStatus === invoiceStatusValues.Submitted ||
        invoiceStatus === invoiceStatusValues.Scheduled ||
        invoiceStatus === invoiceStatusValues.InProcess ||
        invoiceStatus === invoiceStatusValues.InProgress
    ) {
        return invoiceStatusValues.InProgress;
    }

    if (invoiceStatus === invoiceStatusValues.SubmitFailed || invoiceStatus === invoiceStatusValues.Cancelled) {
        return invoiceStatusValues.Error;
    }

    return invoiceStatusValues[invoiceStatus as keyof typeof invoiceStatusValues];
};

const normalizeDate = (input: string): string => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
        return input;
    }

    const parts = input.split("-");
    // eslint-disable-next-line no-magic-numbers
    if (parts.length !== 3) {
        throw new Error("Invalid date format");
    }

    const [day, month, year] = parts;
    const date = new Date(Number(year), new Date(`${month} 1`).getMonth(), Number(day));

    if (Number.isNaN(date.getTime())) {
        throw new TypeError("Invalid date");
    }

    const yyyy = date.getFullYear();
    // eslint-disable-next-line no-magic-numbers
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    // eslint-disable-next-line no-magic-numbers
    const dd = String(date.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
};

const toStringOrUndefined = (rawValue?: string | number | boolean): string | undefined =>
    rawValue !== undefined && rawValue !== null ? rawValue.toString() : undefined;

/**
 * Returns the first truthy value as a string, or undefined if neither is truthy.
 * Useful for preferring one source over another (e.g., saved data over OCR data).
 */
const preferFirst = (primary?: string | number, fallback?: string | number): string | undefined =>
    toStringOrUndefined(primary) || toStringOrUndefined(fallback);
/**
 * Returns the first truthy date, normalizing the fallback if needed.
 */
const preferFirstDate = (primary?: string, fallback?: string): string | undefined =>
    toStringOrUndefined(primary) || (fallback ? normalizeDate(fallback) : undefined);

const convertOptionalCost = (value: string | undefined): number | undefined =>
    value ? convertCostToNumber(value) : undefined;

/**
 * Returns true if any of the provided string values are truthy and not "0".
 */
const hasNonZeroValue = (...values: (string | undefined)[]): boolean => values.some(value => value && value !== "0");

const MINIMUM_FRACTION_DIGITS = 2;
const MAXIMUM_FRACTION_DIGITS = 2;

export const formatCostText = (cost: number, currency: string | undefined): string =>
    `${cost.toLocaleString("en-US", {
        maximumFractionDigits: MAXIMUM_FRACTION_DIGITS,
        minimumFractionDigits: MINIMUM_FRACTION_DIGITS
    })} ${currency ?? ""}`;

export const sumNumberStrings = (string1?: string, string2?: string): string => {
    const num1 = string1 ? Number(string1.replaceAll(",", "")) : 0;
    const num2 = string2 ? Number(string2.replaceAll(",", "")) : 0;
    return (num1 + num2).toString();
};

/**
 * Filters bank accounts based on OCR data match.
 * Returns all accounts if no OCR data or if invoice already has a bank account assigned.
 *
 * Rules:
 * - If no bank accounts, return empty array
 * - If no OCR data, return all bank accounts
 * - If invoice already has a bank account, return all bank accounts (to allow user to switch if OCR was wrong)
 * - If OCR data exists and invoice doesn't have a bank account, return only matching accounts:
 *   -- account number and currency both exist on OCR and both match a single account: return only that exact match
 *   -- account number and currency both exist on OCR but no exact match: return all accounts matching either criterion
 *   -- only account number exists on OCR: return accounts matching account number
 *   -- only currency exists on OCR: return accounts matching currency
 * - If no matches found, return all bank accounts
 */
export const filteredBankAccounts = (
    bankAccounts: BankAccountDto[] | undefined,
    ocrBankAccount?: ocrBankAccountType,
    invoiceBankAccount?: BankAccountDto
): BankAccountDto[] => {
    if (!bankAccounts || !ocrBankAccount || invoiceBankAccount?.accountNumber) {
        return bankAccounts ?? [];
    }

    const { accountNumber, currency } = ocrBankAccount;

    // If both OCR values exist, first check for exact match (both criteria)
    if (accountNumber && currency) {
        const exactMatches = bankAccounts.filter(b => b.accountNumber === accountNumber && b.currency === currency);
        if (exactMatches.length > 0) {
            return exactMatches;
        }
        // No exact match - return accounts matching either criterion
        const partialMatches = bankAccounts.filter(b => b.accountNumber === accountNumber || b.currency === currency);
        return partialMatches.length > 0 ? partialMatches : bankAccounts;
    }

    // Only one OCR value exists - filter by that criterion
    const matches = bankAccounts.filter(b => {
        if (accountNumber) {
            return b.accountNumber === accountNumber;
        }
        if (currency) {
            return b.currency === currency;
        }
        return true;
    });
    return matches.length > 0 ? matches : bankAccounts;
};

export const ocrBankAccountExists = (
    bankAccounts: BankAccountDto[] | undefined,
    ocrBankAccount: { accountNumber?: string; currency?: string } | undefined
): boolean => {
    if (!bankAccounts || !ocrBankAccount) {
        return false;
    }

    const { accountNumber, currency } = ocrBankAccount;
    return bankAccounts.some(
        b =>
            (b.accountNumber === accountNumber && b.currency === currency) ||
            (b.accountNumber === accountNumber && !currency)
    );
};

const pickBankAccount = (
    invoiceDetails: InvoiceDetailsDto,
    ocrData: InvoiceFromOcrDto | undefined,
    bankAccounts: BankAccountDto[] | undefined
): BankAccountDto | undefined => {
    if (invoiceDetails.bankAccount?.accountNumber) {
        return invoiceDetails.bankAccount;
    }

    const ocrBank = ocrData ? { accountNumber: ocrData.bankAccount, currency: ocrData.currency } : undefined;
    const relevant = filteredBankAccounts(bankAccounts, ocrBank);

    if (ocrBank?.accountNumber && relevant.length === 1) {
        return relevant[0];
    }

    return undefined;
};

export const getBranchCompanyNameFromSession = (): string | undefined => {
    const selectedBranch = globalThis.sessionStorage.getItem("selectedBranch") ?? null;
    if (!selectedBranch || selectedBranch === "undefined") {
        return undefined;
    }
    try {
        const parsed = JSON.parse(selectedBranch);
        const name = typeof parsed?.companyName === "string" ? parsed.companyName.trim() : "";
        return name || undefined;
    } catch {
        return undefined;
    }
};

const convertAttachmentToInvoiceAttachment = (attachment: AttachmentDto | undefined): InvoiceAttachment | undefined => {
    if (!attachment) {
        return undefined;
    }

    return {
        filename: attachment.filename,
        fileSize: attachment.fileSize,
        fileExtension: attachment.fileExtension,
        docId: attachment.documentId,
        isUploaded: true,
        isUploading: false,
        uploadFailed: false
    };
};

const getSubclaimRef = ({
    claim,
    invoiceDetails,
    ocrData,
    assignedSubclaimRefs
}: GetSubclaimRefParams): string | undefined => {
    if (!claim) {
        return invoiceDetails.subClaimsRef;
    }

    const proposedId = invoiceDetails.subClaimsRef ?? ocrData?.subClaimsRef;
    const matchingSubclaim = claim.subClaims.find(sc => sc.gardSubClaimRef === proposedId);

    if (matchingSubclaim) {
        return matchingSubclaim.gardSubClaimRef;
    }

    return pickSubClaim(claim.subClaims, assignedSubclaimRefs) ?? undefined;
};

export const convertFormToInsertInvoice = (form: InvoiceFormData, bankAccount?: BankAccountDto): InsertInvoiceDto => ({
    bankAccount,
    invoiceDate: form.invoiceDate?.toString(),
    invoiceDueDate: form.invoiceDueDate?.toString(),
    invoiceNumber: form.invoiceNumber,
    claimsRef: form.claimRef,
    subClaimsRef: form.subClaimsRef,
    correspondentCosts: convertOptionalCost(form.correspondentCosts),
    surveyorCosts: convertOptionalCost(form.surveyorCosts),
    expertCosts: convertOptionalCost(form.expertCosts),
    lawyerFees: convertOptionalCost(form.lawyerFees),
    lawyerExpenses: convertOptionalCost(form.lawyerExpenses),
    counselFees: convertOptionalCost(form.counselFees),
    courtArbitFees: convertOptionalCost(form.courtArbitFees),
    totalVatAmount: convertOptionalCost(form.totalVatAmount),
    totalLocalTax: convertOptionalCost(form.totalLocalTax),
    claimSettlements: convertOptionalCost(form.claimSettlements),
    otherExpenses: convertOptionalCost(form.otherExpenses),
    advanceOfFunds: convertOptionalCost(form.advanceOfFunds),
    previousAdvanceOfFunds: convertOptionalCost(form.previousAdvanceOfFunds),
    correctAmountVerified: form.totalConfirmation,
    vesselName: form.vesselName,
    clientName: form.clientName || "",
    clientVat: form.clientVat || "",
    submittedOnBehalfOfCrmId: globalThis.sessionStorage.getItem("actOnBehalfOf")
        ? JSON.parse(globalThis.sessionStorage.getItem("actOnBehalfOf")!).crmId.toString()
        : "",
    submittedOnBehalfOfCompanyName: globalThis.sessionStorage.getItem("actOnBehalfOf")
        ? JSON.parse(globalThis.sessionStorage.getItem("actOnBehalfOf")!).companyName
        : "",
    branchCompanyCrmId: globalThis.sessionStorage.getItem("selectedBranch")
        ? JSON.parse(globalThis.sessionStorage.getItem("selectedBranch")!).crmId.toString()
        : ""
});

export const convertInvoiceToForm = (
    invoiceDetails: InvoiceDetailsDto,
    ocrData: InvoiceFromOcrDto | undefined,
    claim?: ClaimsDetailsDto,
    bankAccounts?: BankAccountDto[],
    userRole?: string,
    assignedSubclaimRefs?: string[]
): InvoiceFormData | undefined => {
    if (!invoiceDetails) {
        return undefined;
    }

    const subClaimsRef = getSubclaimRef({ claim, invoiceDetails, ocrData, assignedSubclaimRefs });

    const advanceOfFunds = preferFirst(invoiceDetails.advanceOfFunds, ocrData?.advanceOfFunds);
    const previousAdvanceOfFunds = preferFirst(invoiceDetails.previousAdvanceOfFunds, ocrData?.previousAdvanceOfFunds);
    const lawyerFees = preferFirst(invoiceDetails.lawyerFees, ocrData?.lawyerFees);
    const lawyerExpenses = preferFirst(invoiceDetails.lawyerExpenses, ocrData?.lawyerExpenses);
    const counselFees = preferFirst(invoiceDetails.counselFees);
    const courtArbitFees = preferFirst(invoiceDetails.courtArbitFees);
    const bankAccount = pickBankAccount(invoiceDetails, ocrData, bankAccounts);
    const invoiceDueDate = preferFirstDate(invoiceDetails.invoiceDueDate, ocrData?.invoiceDueDate);

    const hasLawyerAndCounselFees = hasNonZeroValue(lawyerFees, lawyerExpenses, counselFees, courtArbitFees);
    const hasAdvanceOfFunds = hasNonZeroValue(advanceOfFunds, previousAdvanceOfFunds);

    if (userRole === "Client") {
        return {
            bankAccount,
            invoiceDueDate,
            subClaimsRef,
            hasLawyerAndCounselFees,
            hasAdvanceOfFunds,
            totalConfirmation: false,
            addressConfirmation: false,
            personalSensitiveConfirmation: false,
            clientVat: invoiceDetails.clientVat,
            claimRef: claim?.id ?? invoiceDetails.claimsRef,
            invoiceDate: preferFirstDate(invoiceDetails.invoiceDate, ocrData?.invoiceDate),
            invoiceNumber: preferFirst(invoiceDetails.invoiceNumber, ocrData?.invoiceNumber),
            claimSettlements: preferFirst(invoiceDetails.claimSettlements, ocrData?.totalCosts),
            invoiceAttachment: convertAttachmentToInvoiceAttachment(invoiceDetails.attachment),
            vesselName: preferFirst(claim?.vessel, invoiceDetails.vesselName),
            clientName: preferFirst(claim?.clientName, invoiceDetails.clientName),
            submittedOnBehalfOfCrmId: toStringOrUndefined(invoiceDetails.submittedOnBehalfOfCrmId),
            submittedOnBehalfOfCompanyName: toStringOrUndefined(invoiceDetails.submittedOnBehalfOfCompanyName),
            branchCompanyCrmId: toStringOrUndefined(invoiceDetails.branchCompanyCrmId),
            correspondentCosts: "",
            surveyorCosts: "",
            expertCosts: "",
            lawyerFees: "",
            lawyerExpenses: "",
            counselFees: "",
            courtArbitFees: "",
            totalVatAmount: "",
            totalLocalTax: "",
            otherExpenses: "",
            advanceOfFunds: "",
            previousAdvanceOfFunds: ""
        };
    }

    return {
        bankAccount,
        invoiceDueDate,
        lawyerExpenses,
        lawyerFees,
        advanceOfFunds,
        previousAdvanceOfFunds,
        counselFees,
        courtArbitFees,
        hasAdvanceOfFunds,
        hasLawyerAndCounselFees,
        subClaimsRef,
        totalConfirmation: false,
        addressConfirmation: false,
        personalSensitiveConfirmation: false,
        clientVat: invoiceDetails.clientVat,
        claimRef: claim?.id ?? invoiceDetails.claimsRef,
        correspondentCosts: preferFirst(invoiceDetails.correspondentCosts, ocrData?.correspondentCosts),
        expertCosts: preferFirst(invoiceDetails.expertCosts, ocrData?.expertCosts),
        invoiceDate: preferFirstDate(invoiceDetails.invoiceDate, ocrData?.invoiceDate),
        invoiceNumber: preferFirst(invoiceDetails.invoiceNumber, ocrData?.invoiceNumber),
        surveyorCosts: preferFirst(invoiceDetails.surveyorCosts, ocrData?.surveyorCosts),
        totalLocalTax: preferFirst(invoiceDetails.totalLocalTax, ocrData?.totalLocalTax),
        totalVatAmount: preferFirst(invoiceDetails.totalVatAmount, ocrData?.totalVatAmount),
        otherExpenses: preferFirst(invoiceDetails.otherExpenses, ocrData?.otherExpenses),
        claimSettlements: toStringOrUndefined(invoiceDetails.claimSettlements),
        invoiceAttachment: convertAttachmentToInvoiceAttachment(invoiceDetails.attachment),
        vesselName: preferFirst(claim?.vessel, invoiceDetails.vesselName),
        clientName: preferFirst(claim?.clientName, invoiceDetails.clientName),
        submittedOnBehalfOfCrmId: toStringOrUndefined(invoiceDetails.submittedOnBehalfOfCrmId),
        submittedOnBehalfOfCompanyName: toStringOrUndefined(invoiceDetails.submittedOnBehalfOfCompanyName),
        branchCompanyCrmId: toStringOrUndefined(invoiceDetails.branchCompanyCrmId)
    };
};
export const StatusBadge = ({ id, invoiceStatus, size }: StatusBadgeProps) => {
    switch (getDisplayInvoiceStatus(invoiceStatus)) {
        case invoiceStatusValues.Draft:
            return (
                <Badge id={id} variant="warning" size={size}>
                    {i18n.t("invoices:invoiceStatuses.draft")}
                </Badge>
            );
        case invoiceStatusValues.Error:
            return (
                <Badge id={id} variant="danger" size={size}>
                    {i18n.t("invoices:invoiceStatuses.error")}
                </Badge>
            );
        case invoiceStatusValues.Rejected:
            return (
                <Badge id={id} variant="danger" size={size}>
                    {i18n.t("invoices:invoiceStatuses.rejected")}
                </Badge>
            );
        case invoiceStatusValues.InProgress:
            return (
                <Badge id={id} variant="info" size={size}>
                    {i18n.t("invoices:invoiceStatuses.inProgress")}
                </Badge>
            );
        case invoiceStatusValues.Approved:
            return (
                <Badge id={id} variant="success" size={size}>
                    {i18n.t("invoices:invoiceStatuses.approved")}
                </Badge>
            );
        default:
            return (
                <Badge id={id} size={size} variant="neutral">
                    {invoiceStatus}
                </Badge>
            );
    }
};

export const useInvoiceEventRedirect = (navigate: ReturnType<typeof useNavigate>) =>
    useBroadcastChannel("invoice_channel", data => {
        if (
            data.type === "INVOICE_SCHEDULED" ||
            data.type === "INVOICE_SUBMITTED" ||
            data.type === "INVOICE_DELETED" ||
            data.type === "INVOICE_IN_SUBMISSION"
        ) {
            navigate("/");
        }
    });
